import { IconProp } from '@fortawesome/fontawesome-svg-core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useUnitSymbols } from '../../../../hooks/use-unit-symbols';
import { Icon } from '../../../icon/icon';

/**
 * A surface straight above or below the snapped point, see findHeightLayers.
 */
export type TooltipHeight = {
  /** The height, in project coordinates. */
  height: number;
  source: 'pointCloud' | 'tiledModel' | 'asset' | 'snap';
  /** The name of the asset that it is on. */
  name?: string;
  /** Whether the current point is snapped to it. */
  snapped: boolean;
  /** How much higher it is than the next one below. */
  delta?: number;
};

const icons: Record<TooltipHeight['source'], IconProp> = {
  pointCloud: ['fal', 'cloud'],
  tiledModel: ['fal', 'cube'],
  asset: ['fal', 'cubes'],
  snap: ['fal', 'crosshairs'],
};

/**
 * Lists the heights of the surfaces straight above and below the snapped point, highest first,
 * with what each is and the difference in height between them. The snapped one is marked.
 */
export const HeightLayers = (props: { heights: TooltipHeight[] }) => {
  const { t } = useTranslation();
  const unitSymbols = useUnitSymbols();
  const sourceNames: Record<TooltipHeight['source'], string> = {
    pointCloud: t('assetPositionTooltip.heightLayers.pointCloud', { ns: 'skyview' }),
    tiledModel: t('assetPositionTooltip.heightLayers.tiledModel', { ns: 'skyview' }),
    asset: t('assetPositionTooltip.heightLayers.asset', { ns: 'skyview' }),
    snap: t('assetPositionTooltip.heightLayers.snap', { ns: 'skyview' }),
  };

  return (
    <Component data-testid="height-layers">
      <label>{t('assetPositionTooltip.heightLayers.title', { ns: 'skyview' })}</label>
      <List>
        {props.heights.map((layer, i) => (
          <React.Fragment key={i}>
            <Layer data-snapped={layer.snapped} snapped={layer.snapped}>
              <Icon
                fixedWidth={true}
                icon={icons[layer.source]}
                title={sourceNames[layer.source]}
              />
              <span>
                {layer.height.toFixed(2)} {unitSymbols.length}
              </span>
              <Name title={layer.name ?? sourceNames[layer.source]}>
                {layer.name ?? sourceNames[layer.source]}
              </Name>
              {layer.snapped && (
                <Snapped title={t('assetPositionTooltip.heightLayers.snapped', { ns: 'skyview' })}>
                  <Icon fixedWidth={true} icon={['fas', 'caret-left']} />
                </Snapped>
              )}
            </Layer>
            {layer.delta !== undefined && (
              <Delta>
                {/* The space between the layers, like the dashed line in the view. */}
                <Gap />
                <span>
                  Δ {layer.delta.toFixed(2)} {unitSymbols.length}
                </span>
              </Delta>
            )}
          </React.Fragment>
        ))}
      </List>
    </Component>
  );
};

const Component = styled.div`
  margin-top: 0.5em;
  padding-top: 0.5em;
  // Faded rather than colored, so that it suits a light background in the menu too.
  border-top: 1px solid rgba(128, 128, 128, 0.6);

  > label {
    display: block;
    opacity: 0.67;
    font-size: 0.9em;
    margin-bottom: 0.3em;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Layer = styled.div<{ snapped: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.4em;
  padding: 0.15em 0;
  white-space: nowrap;
  font-weight: ${(props) => (props.snapped ? 600 : 'normal')};
`;

// Long names of assets end in an ellipsis.
const Name = styled.span`
  min-width: 0;
  max-width: 14em;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.67;
  font-weight: normal;
`;

const Snapped = styled.span`
  color: ${(props) => props.theme.color.brand.dark};
`;

const Delta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4em;
  white-space: nowrap;

  > span {
    opacity: 0.67;
    font-size: 0.85em;
  }
`;

// Under the icons, which are 1.25em wide.
const Gap = styled.div`
  width: 1.25em;
  height: 1.6em;
  flex-shrink: 0;
  background: linear-gradient(currentColor 60%, transparent 0) center / 2px 5px repeat-y;
  opacity: 0.67;
`;
